import React from 'react';
import Uppy from '@uppy/core';
import { Comment } from 'antd';
import classNames from 'classnames';
import {
  AssetVersionCommentDto,
  AssetVersionCommentGroupMentionDto,
  AssetVersionCommentMentionDto
} from '@api/Api';
import useTypedDispatch from '@hooks/useTypedDispatch';
import { openTaskModal } from '@redux/reducers/tasks';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import CommentEditor from '@pages/MediaViewer/Comments/components/CommentEditor';
import type { SubmitEditorValues } from '@pages/MediaViewer/Comments/components/CommentEditor/CommentEditor';
import CommentItem from '@pages/MediaViewer/Comments/components/CommentItem';
import { useAssetMVPermissions, useTypedSelector } from '@hooks';
import { taskStateSelector } from '@redux/selectors/tasks';
import { buildCommentActions } from '@pages/MediaViewer/Comments/buildCommentActions';
import CommentViewReplies from '@pages/MediaViewer/Comments/CommentViewReplies';

type CommentType = AssetVersionCommentDto & {
  level: number;
  children: CommentType[];
};

interface CommentTreeProps {
  commentMentions: boolean;
  comment: CommentType;
  editCommentId: string | null;
  selectedCommentId: string | null;
  replyToCommentId: string | null;
  showReplyOfCommentId: string | null;
  isCheckboxChecked: boolean;
  checkboxValueType: 'timeCode' | 'pageNumber' | null;
  potentialMentions: AssetVersionCommentMentionDto[];
  potentialGroupMentions: AssetVersionCommentGroupMentionDto[];
  searchPotentialMentions: (search: string) => void;
  onReply: (comment: CommentType) => void;
  onLikeClick: (comment: CommentType) => void;
  onResolveClick: (comment: AssetVersionCommentDto) => void;
  onShowReplyClick: (comment: CommentType) => void;
  onAddComment: (values: SubmitEditorValues) => Promise<void>;
  setEditCommentId: (commentId: string | null) => void;
  setReplyToCommentId: (commentId: string | null) => void;
  setIsCheckboxChecked: (checked: boolean) => void;
  onEditComment: (values: SubmitEditorValues) => Promise<void>;
  onDeleteComment: (commentId: string) => void;
  onTurnCommentIntoTask: (comment: AssetVersionCommentDto) => void;
  canCreateTask: boolean;
  isEmbeddedWebView?: boolean;
  uppy: Uppy;
}

export default function CommentTree(props: CommentTreeProps) {
  const {
    commentMentions,
    comment,
    editCommentId,
    selectedCommentId,
    showReplyOfCommentId,
    replyToCommentId,
    isCheckboxChecked,
    checkboxValueType,
    potentialMentions,
    potentialGroupMentions,
    searchPotentialMentions,
    onReply,
    onLikeClick,
    onResolveClick,
    onShowReplyClick,
    onAddComment,
    setEditCommentId,
    setReplyToCommentId,
    setIsCheckboxChecked,
    onEditComment,
    onDeleteComment,
    onTurnCommentIntoTask,
    canCreateTask,
    isEmbeddedWebView = false,
    uppy
  } = props;
  const dispatch = useTypedDispatch();
  const canLeaveComment = useAssetMVPermissions('comment');
  const taskId = useTypedSelector((state) => {
    if (!comment.task?.id) return null;
    const taskState = taskStateSelector(state, { taskId: comment.task.id });
    if (!taskState.entity) return comment.task.id;
    return taskState.entity.assetVersionComment?.id === comment.id
      ? comment.task.id
      : null;
  });

  const {
    selectComment,
    setCanvasMode,
    setActivePageNumber,
    setDocumentHighlight,
    setMobileSidebarCommentEditing
  } = useAssetViewerState();

  const onSelectComment = () => {
    if (comment.isCopied) return;
    setEditCommentId('');
    setReplyToCommentId(null);
    setMobileSidebarCommentEditing(false);
    selectComment(comment.id);
    setCanvasMode('view');
    if (comment.documentPage) setActivePageNumber(comment.documentPage);
    setDocumentHighlight(
      comment.documentHighlight
        ? {
            text: comment.documentHighlight,
            rects: comment.documentHighlightRects
          }
        : null
    );
    if (comment.documentHighlight) setCanvasMode('view');
    document
      .querySelector(`.media_viewer_content [data-comment-id="${comment.id}"]`)
      ?.scrollIntoView({
        behavior: 'instant',
        block: 'start'
      });
  };

  const canLikeComment = useAssetMVPermissions('likeComments');
  const actions = buildCommentActions({
    comment,
    taskId,
    canLeaveComment,
    canLikeComment,
    onOpenTask: () => {
      if (!taskId) return;
      dispatch(
        openTaskModal({
          taskId,
          descriptionPlaceholder:
            'Describe your task so your teammates know what to do.'
        })
      );
    },
    onReply: () => onReply(comment),
    onLikeClick: () => onLikeClick(comment),
    isEmbeddedWebView
  });

  const repliesCount = comment.children?.length ?? 0;

  return (
    <Comment
      key={comment.id}
      actions={actions}
      className={classNames(
        (comment.id === selectedCommentId || comment.id === replyToCommentId) &&
          'selected_comment',
        comment.id === replyToCommentId && 'is_show_reply_form',
        {
          'b-copy-comment': comment.isCopied
        }
      )}
      content={
        <div>
          {comment.id === editCommentId ? (
            <CommentEditor
              commentMentions={commentMentions}
              comment={comment}
              editorFor="edit"
              editCommentId={comment.id}
              setEditCommentId={setEditCommentId}
              replyToCommentId={replyToCommentId}
              setReplyToCommentId={setReplyToCommentId}
              isFocused={true}
              onSubmitEditor={onEditComment}
              isCheckboxChecked={isCheckboxChecked}
              setIsCheckboxChecked={setIsCheckboxChecked}
              checkboxValueType={checkboxValueType}
              potentialMentions={potentialMentions}
              potentialGroupMentions={potentialGroupMentions}
              searchPotentialMentions={searchPotentialMentions}
              uppy={uppy}
            />
          ) : (
            <div
              data-comment-id={comment.id}
              style={{ scrollMarginTop: '90px' }}
            >
              <CommentItem
                isSidebarCommentItem
                setEditCommentId={setEditCommentId}
                setReplyToCommentId={setReplyToCommentId}
                comment={comment}
                onDeleteComment={onDeleteComment}
                onClickComment={onSelectComment}
                onResolveClick={onResolveClick}
                canCreateTask={canCreateTask}
                isEmbeddedWebView={isEmbeddedWebView}
                onTurnCommentIntoTask={onTurnCommentIntoTask}
              />
            </div>
          )}
        </div>
      }
    >
      <>
        {comment.id === replyToCommentId && (
          <CommentEditor
            commentMentions={commentMentions}
            comment={comment}
            editorFor="reply"
            editCommentId={null}
            setEditCommentId={setEditCommentId}
            replyToCommentId={replyToCommentId}
            setReplyToCommentId={setReplyToCommentId}
            isFocused={true}
            onSubmitEditor={onAddComment}
            isCheckboxChecked={isCheckboxChecked}
            setIsCheckboxChecked={setIsCheckboxChecked}
            checkboxValueType={checkboxValueType}
            potentialMentions={potentialMentions}
            potentialGroupMentions={potentialGroupMentions}
            searchPotentialMentions={searchPotentialMentions}
            uppy={uppy}
          />
        )}
        {repliesCount > 0 && (
          <CommentViewReplies
            count={repliesCount}
            isExpanded={comment.id === showReplyOfCommentId}
            onClick={() => onShowReplyClick(comment)}
          />
        )}
        {comment.id === showReplyOfCommentId &&
          comment.children.map((comment) => (
            <CommentTree
              commentMentions={commentMentions}
              key={comment.id}
              comment={comment}
              selectedCommentId={selectedCommentId}
              editCommentId={editCommentId}
              replyToCommentId={replyToCommentId}
              showReplyOfCommentId={showReplyOfCommentId}
              isCheckboxChecked={isCheckboxChecked}
              checkboxValueType={checkboxValueType}
              onLikeClick={onLikeClick}
              onResolveClick={onResolveClick}
              onReply={onReply}
              onShowReplyClick={onShowReplyClick}
              onAddComment={onAddComment}
              setEditCommentId={setEditCommentId}
              setReplyToCommentId={setReplyToCommentId}
              setIsCheckboxChecked={setIsCheckboxChecked}
              onEditComment={onEditComment}
              onDeleteComment={onDeleteComment}
              onTurnCommentIntoTask={onTurnCommentIntoTask}
              canCreateTask={canCreateTask}
              isEmbeddedWebView={isEmbeddedWebView}
              potentialMentions={potentialMentions}
              potentialGroupMentions={potentialGroupMentions}
              searchPotentialMentions={searchPotentialMentions}
              uppy={uppy}
            />
          ))}
      </>
    </Comment>
  );
}
